import React, {
  useEffect,
  useState,
} from "react";

import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from "framer-motion";

import {
  useLocation,
} from "react-router-dom";


/* =========================================================
   CONFIG
========================================================= */

/*
  Sacred transition kitni der visible rahe.

  4000 = 4 seconds
*/
const SACRED_TRANSITION_MS =
  1000;


/* =========================================================
   SACRED PAGE TRANSITION
========================================================= */

export default function SacredPageTransition({
  children,
}) {

  const location =
    useLocation();

  const reduceMotion =
    useReducedMotion();


  const [
    showSacredIntro,
    setShowSacredIntro,
  ] = useState(true);


  /* =======================================================
     RUN AGAIN ON EVERY PAGE CHANGE
  ======================================================= */

  useEffect(() => {

    /*
      Har route change par
      transition dubara start hoga.
    */

    setShowSacredIntro(
      true
    );


    const timer =
      window.setTimeout(
        () => {

          setShowSacredIntro(
            false
          );

        },

        SACRED_TRANSITION_MS
      );


    return () => {

      window.clearTimeout(
        timer
      );

    };

  }, [
    location.pathname,
    location.search,
  ]);


  return (

    <div
      className="
        relative

        min-h-0

        w-full
      "
    >

      {/* ===================================================
          ACTUAL PAGE

          4 sec ke baad hi clean fade-in.
      =================================================== */}

      <motion.div

        key={
          `page-${location.pathname}-${location.search}`
        }

        initial={
          reduceMotion
            ? {
                opacity: 1,
              }
            : {
                opacity: 0,
                y: 8,
              }
        }

        animate={
          showSacredIntro
            ? {
                opacity: 0,
                y: 8,
              }
            : {
                opacity: 1,
                y: 0,
              }
        }

        transition={{
          duration:
            reduceMotion
              ? 0
              : 0.42,

          ease: [
            0.22,
            1,
            0.36,
            1,
          ],
        }}

        className="
          relative

          w-full
        "
      >

        {children}

      </motion.div>


      {/* ===================================================
          FULL SCREEN SACRED INTRO
      =================================================== */}

      <AnimatePresence>

        {showSacredIntro && (

          <motion.div

            key={
              `sacred-intro-${location.pathname}-${location.search}`
            }

            initial={{
              opacity: 1,
            }}

            animate={{
              opacity: 1,
            }}

            exit={{
              opacity: 0,
            }}

            transition={{
              exit: {
                duration: 0.38,

                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              },
            }}

            className="
              fixed
              inset-0

              z-[2147483000]

              flex

              min-h-[100dvh]

              items-center
              justify-center

              overflow-hidden

              bg-[#fffdf8]

              dark:bg-[#050810]
            "

            aria-label="Opening GurSetu"

          >

            {/* ===============================================
                SUBTLE BACKGROUND
            =============================================== */}

            <div
              className="
                pointer-events-none

                absolute
                inset-0

                bg-[radial-gradient(circle_at_50%_44%,rgba(245,158,11,0.10),transparent_34%)]

                dark:bg-[radial-gradient(circle_at_50%_44%,rgba(245,158,11,0.085),transparent_35%)]
              "
            />


            {/* ===============================================
                TOP GOLD AMBIENT LIGHT
            =============================================== */}

            {!reduceMotion && (

              <motion.div

                initial={{
                  opacity: 0,
                  scale: 0.65,
                }}

                animate={{
                  opacity: [
                    0,
                    0.20,
                    0.13,
                    0.20,
                    0.11,
                  ],

                  scale: [
                    0.65,
                    1,
                    1.08,
                    1,
                    1.12,
                  ],
                }}

                transition={{
                  duration: 4,

                  times: [
                    0,
                    0.22,
                    0.5,
                    0.75,
                    1,
                  ],

                  ease:
                    "easeInOut",
                }}

                className="
                  pointer-events-none

                  absolute

                  left-1/2
                  top-1/2

                  h-60
                  w-60

                  -translate-x-1/2
                  -translate-y-1/2

                  rounded-full

                  bg-amber-300/25

                  blur-[70px]

                  dark:bg-amber-300/[0.11]
                "
              />

            )}


            {/* ===============================================
                MAIN BRAND
            =============================================== */}

            <div
              className="
                relative
                z-10

                flex
                flex-col

                items-center
                justify-center

                px-6

                text-center
              "
            >

              {/* =============================================
                  IK ONKAR
              ============================================= */}

              <motion.div

                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity: 0,
                        scale: 0.45,
                        rotateY: -90,
                        y: 12,
                      }
                }

                animate={{
                  opacity: 1,
                  scale: 1,
                  rotateY: 0,
                  y: 0,
                }}

                transition={{
                  duration: 0.8,

                  ease: [
                    0.22,
                    1,
                    0.36,
                    1,
                  ],
                }}

                className="
                  relative

                  flex

                  items-center
                  justify-center
                "
              >

                {/* ONKAR SOFT HALO */}

                {!reduceMotion && (

                  <motion.span

                    animate={{
                      opacity: [
                        0.12,
                        0.30,
                        0.12,
                      ],

                      scale: [
                        0.86,
                        1.18,
                        0.86,
                      ],
                    }}

                    transition={{
                      duration: 2.7,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}

                    className="
                      pointer-events-none

                      absolute

                      h-20
                      w-20

                      rounded-full

                      bg-amber-300/25

                      blur-2xl

                      dark:bg-amber-300/[0.13]
                    "
                  />

                )}


                <span
                  className="
                    relative
                    z-10

                    font-black

                    text-[40px]

                    leading-none

                    text-amber-500

                    drop-shadow-[0_0_16px_rgba(245,158,11,0.25)]

                    min-[390px]:text-[44px]
                  "
                >
                  ੴ
                </span>

              </motion.div>


              {/* =============================================
                  SMALL SACRED LABEL
              ============================================= */}

              <motion.p

                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity: 0,
                        y: 5,
                      }
                }

                animate={{
                  opacity: 1,
                  y: 0,
                }}

                transition={{
                  duration: 0.5,
                  delay: 0.42,
                }}

                className="
                  mt-4

                  text-[8px]

                  font-black

                  uppercase

                  tracking-[3.2px]

                  text-amber-700/55

                  dark:text-amber-300/50
                "
              >

                YOUR SPIRITUAL COMPANION

              </motion.p>


              {/* =============================================
                  GURSETU WORDMARK
              ============================================= */}

              <motion.div

                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity: 0,
                        scale: 0.92,
                        y: 10,
                      }
                }

                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                }}

                transition={{
                  duration: 0.7,
                  delay: 0.52,

                  ease: [
                    0.22,
                    1,
                    0.36,
                    1,
                  ],
                }}

                className="
                  relative

                  mt-1

                  overflow-hidden

                  px-3
                  py-1
                "
              >

                {/* WORD */}

                <h1
                  className="
                    relative
                    z-10

                    font-heading

                    text-[42px]

                    font-black

                    leading-none

                    tracking-[-1px]

                    text-amber-500

                    min-[390px]:text-[48px]

                    sm:text-[54px]
                  "

                  style={{
                    textShadow:
                      "0 5px 22px rgba(245,158,11,0.16)",
                  }}
                >

                  GurSetu

                </h1>


                {/* ===========================================
                    SLOW CINEMATIC SHINE

                    4 sec me ek clean sweep.
                =========================================== */}

                {!reduceMotion && (

                  <motion.div

                    initial={{
                      x: "-180%",
                    }}

                    animate={{
                      x: "260%",
                    }}

                    transition={{
                      duration: 3.2,
                      delay: 0.55,
                      ease: "easeInOut",
                    }}

                    className="
                      pointer-events-none

                      absolute

                      -top-[35%]

                      h-[180%]
                      w-16

                      -skew-x-[20deg]

                      bg-gradient-to-r

                      from-transparent

                      via-[#fff2b2]/75

                      to-transparent

                      blur-[3px]
                    "
                  />

                )}

              </motion.div>


              {/* =============================================
                  GOLD LINE
              ============================================= */}

              <motion.div

                initial={
                  reduceMotion
                    ? false
                    : {
                        scaleX: 0,
                        opacity: 0,
                      }
                }

                animate={{
                  scaleX: 1,
                  opacity: 1,
                }}

                transition={{
                  duration: 0.8,
                  delay: 0.8,

                  ease: [
                    0.22,
                    1,
                    0.36,
                    1,
                  ],
                }}

                className="
                  mt-3

                  h-px
                  w-36

                  origin-center

                  bg-gradient-to-r

                  from-transparent

                  via-amber-400/90

                  to-transparent

                  shadow-[0_0_12px_rgba(245,158,11,0.34)]
                "
              />


              {/* =============================================
                  BOTTOM MESSAGE
              ============================================= */}

              <motion.p

                initial={{
                  opacity: 0,
                }}

                animate={{
                  opacity: 1,
                }}

                transition={{
                  duration: 0.6,
                  delay: 1.05,
                }}

                className="
                  mt-4

                  text-[9px]

                  font-semibold

                  tracking-[0.7px]

                  text-slate-400

                  dark:text-slate-500
                "
              >

                Connect • Learn • Grow Spiritually

              </motion.p>


              {/* =============================================
                  THREE QUIET DOTS
              ============================================= */}

              {!reduceMotion && (

                <div
                  className="
                    mt-5

                    flex

                    items-center
                    justify-center

                    gap-2
                  "
                >

                  {[0, 1, 2].map(
                    (dot) => (

                      <motion.span

                        key={dot}

                        animate={{
                          opacity: [
                            0.20,
                            1,
                            0.20,
                          ],

                          scale: [
                            0.7,
                            1.05,
                            0.7,
                          ],
                        }}

                        transition={{
                          duration: 1.2,
                          repeat: Infinity,
                          delay:
                            dot * 0.16,
                        }}

                        className="
                          h-1
                          w-1

                          rounded-full

                          bg-amber-500
                        "
                      />

                    )
                  )}

                </div>

              )}

            </div>


            {/* ===============================================
                FINAL CLEAN WHITE/GOLD FLASH

                Transition ke end me halka cinematic flash.
            =============================================== */}

            {!reduceMotion && (

              <motion.div

                initial={{
                  opacity: 0,
                }}

                animate={{
                  opacity: [
                    0,
                    0,
                    0,
                    0.14,
                    0,
                  ],
                }}

                transition={{
                  duration: 4,

                  times: [
                    0,
                    0.70,
                    0.84,
                    0.92,
                    1,
                  ],

                  ease:
                    "easeInOut",
                }}

                className="
                  pointer-events-none

                  absolute
                  inset-0

                  bg-amber-50

                  dark:bg-amber-300/[0.08]
                "
              />

            )}


            {/* ===============================================
                BOTTOM PROGRESS LINE
            =============================================== */}

            {!reduceMotion && (

              <div
                className="
                  pointer-events-none

                  absolute

                  bottom-0
                  left-0
                  right-0

                  h-[2px]

                  bg-amber-900/5

                  dark:bg-white/[0.025]
                "
              >

                <motion.div

                  initial={{
                    scaleX: 0,
                  }}

                  animate={{
                    scaleX: 1,
                  }}

                  transition={{
                    duration: 4,
                    ease: "linear",
                  }}

                  style={{
                    transformOrigin:
                      "left center",
                  }}

                  className="
                    h-full
                    w-full

                    bg-gradient-to-r

                    from-amber-300

                    via-amber-500

                    to-orange-400
                  "
                />

              </div>

            )}

          </motion.div>

        )}

      </AnimatePresence>

    </div>

  );
}